import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, MapPin, Phone } from 'lucide-react';

const FooterContainer = styled.footer`
  background-color: var(--bg-secondary);
  position: relative;
  overflow: hidden;
  border-top: 1px solid var(--border-subtle);
  padding: 5rem 0 2rem;
  margin-top: auto;
`;

const FooterGlow = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 50%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--accent-primary), transparent);
  box-shadow: 0 0 40px 2px var(--accent-primary);
  opacity: 0.3;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 4rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const FooterSection = styled.div`
  h3 {
    color: var(--text-primary);
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  p {
    color: var(--text-secondary);
    line-height: 1.7;
    margin-bottom: 1rem;
    max-width: 400px;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  
  li {
    margin-bottom: 0.8rem;
    
    a {
      color: var(--text-secondary);
      transition: all 0.3s ease;
      display: inline-block;
      
      &:hover {
        color: var(--text-primary);
        transform: translateX(4px);
      }
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--text-secondary);
  margin-bottom: 1rem;

  svg {
    color: var(--accent-primary);
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid var(--border-subtle);
  padding-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-tertiary);
  font-size: 0.9rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  
  a {
    color: var(--text-tertiary);
    &:hover {
      color: var(--accent-primary);
    }
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterGlow />
      <div className="container" style={{ maxWidth: '1100px' }}>
        <FooterContent>
          <FooterSection>
            <h3 className="gradient-text-accent" style={{ fontSize: '1.5rem' }}>ContextJet.Ai</h3>
            <p>
              Unlock insights and automate processes with our next-generation
              AI-powered NLP solutions. Transform your business with intelligent
              automation that scales.
            </p>
          </FooterSection>

          <FooterSection>
            <h3>Quick Links</h3>
            <FooterLinks>
              <li><Link to="/solutions">Our Solutions</Link></li>
              <li><Link to="/case-studies">Success Stories</Link></li>
              <li><Link to="/about">About the Team</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><Link to="/contact">Get in Touch</Link></li>
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <h3>Legal</h3>
            <FooterLinks>
              <li><Link to="/terms-and-conditions">Terms and Conditions</Link></li>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/refund-cancellation-policy">Refund &amp; Cancellation Policy</Link></li>
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <h3>Contact</h3>
            <ContactItem>
              <Mail size={18} />
              <span>takeoff@contextjetai.services</span>
            </ContactItem>
            <ContactItem>
              <MapPin size={18} />
              <span>Registered in India &middot; Serving clients globally</span>
            </ContactItem>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <p>&copy; {new Date().getFullYear()} ContextJetAi Services LLP. All rights reserved. Registered in India.</p>
          <SocialLinks>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-and-conditions">Terms of Service</Link>
          </SocialLinks>
        </FooterBottom>
      </div>
    </FooterContainer>
  );
};

export default Footer;